export const ATTACHMENTS_BUCKET =
  import.meta.env.VITE_ATTACHMENTS_BUCKET || "attachments";

/** Validity (in seconds) requested for note attachment signed URLs. */
export const ATTACHMENT_SIGNED_URL_TTL_SECONDS = 3600;

/**
 * How a stored attachment must be opened: `direct` URLs are used as-is, while
 * `storage` paths must first be exchanged for a short-lived signed URL.
 */
export type AttachmentTarget =
  | { kind: "direct"; url: string }
  | { kind: "storage"; path: string };

const hasUrlScheme = (value: string) => /^[a-z][a-z0-9+.-]*:/i.test(value);

/**
 * Extracts the storage object path from a legacy public URL of the attachments
 * bucket (`<supabase url>/storage/v1/object/public/<bucket>/<path>`).
 *
 * @returns The decoded object path, or `null` when the URL is not such a URL.
 */
export const extractLegacyPublicPath = (
  src: string,
  supabaseUrl: string | undefined,
  bucket: string = ATTACHMENTS_BUCKET,
): string | null => {
  if (!supabaseUrl) {
    return null;
  }
  const prefix = `${supabaseUrl.replace(/\/+$/, "")}/storage/v1/object/public/${bucket}/`;
  if (!src.startsWith(prefix)) {
    return null;
  }
  const rawPath = src.slice(prefix.length).split(/[?#]/)[0];
  if (!rawPath) {
    return null;
  }
  try {
    return decodeURIComponent(rawPath);
  } catch {
    return rawPath;
  }
};

/**
 * Classifies a stored attachment (`src` + optional `path`) into the thing the
 * UI should open: blob:/data: sources and foreign URLs pass through unchanged,
 * legacy public URLs of the attachments bucket and plain paths become storage
 * paths to sign.
 */
export const getAttachmentTarget = (
  src: string | undefined,
  path: string | undefined,
  supabaseUrl: string | undefined = import.meta.env.VITE_SUPABASE_URL,
): AttachmentTarget | null => {
  if (src) {
    if (src.startsWith("blob:") || src.startsWith("data:")) {
      return { kind: "direct", url: src };
    }
    const legacyPath = extractLegacyPublicPath(src, supabaseUrl);
    if (legacyPath) {
      return { kind: "storage", path: legacyPath };
    }
    if (hasUrlScheme(src)) {
      // A foreign-host URL with a stored object path: sign the path instead.
      if (path && /^https?:/i.test(src)) {
        return { kind: "storage", path };
      }
      return { kind: "direct", url: src };
    }
    return { kind: "storage", path: src };
  }
  return path ? { kind: "storage", path } : null;
};

/**
 * Resolves a stored attachment to a URL the browser can open.
 *
 * @param sign - Exchanges a storage path for a signed URL (`null` on failure).
 * @returns The URL to open, or `null` when there is none or signing failed.
 */
export const resolveAttachmentUrl = async (
  src: string | undefined,
  path: string | undefined,
  sign: (path: string) => Promise<string | null>,
  supabaseUrl?: string,
): Promise<string | null> => {
  const target = getAttachmentTarget(src, path, supabaseUrl);
  if (!target) {
    return null;
  }
  return target.kind === "direct" ? target.url : sign(target.path);
};
